// Keycloak API client
// It periodically does health check
import axios from 'axios'
import keycloakConfig from '@/config/keycloak_config'
import { queryClient } from '@/queryClient'
import router from '@/router'
import { ErrorCode } from '@/errors/codes'
import { errorPathForCode } from '@/errors/code_to_error_route'
import { v7 as uuidv7 } from 'uuid'

const keycloakHealthHttp = axios.create({
  baseURL: keycloakConfig.healthCheckUrl,
})

// HEALTH CHECK

interface HealthCheckItem {
  // One entry in the Keycloak `/health` JSON `checks` array.

  name: string
  // Display name of the health check (e.g. database connections).

  status: string
  // Status for this check (e.g. UP / DOWN).
}

interface HealthCheckResponse {
  // Shape of GET Keycloak `/health` used by `healthCheck()`.

  status: string
  // Overall health (e.g. UP when the server considers itself healthy).

  checks: HealthCheckItem[]
  // Individual async health checks included in the response.
}

export async function healthCheck(): Promise<boolean> {
  const { data } = await keycloakHealthHttp.get<HealthCheckResponse>('/health')
  return data.status === 'UP'
}

/**
 * Redirect browser to Keycloak login page.
 *
 * `prompt=login` forces Keycloak to re-authenticate even if it still has an active SSO
 * session for this browser, instead of silently re-approving whoever was last logged in.
 * Without it, logging out only clears our own session cookies (see `clear_session_cookies`
 * on the backend) — Keycloak's own session cookie is untouched, so the very next login
 * redirect would otherwise sign the user straight back in as the same account with no
 * credential prompt, making it impossible to switch accounts.
 */
export function redirectToLogin(): void {
  const healthy = queryClient.getQueryData<boolean>(['keycloakHealth'])
  if (healthy !== true) {
    router.push({ path: errorPathForCode(ErrorCode.KeycloakUnavailable) })
    return
  }
  const nonce = String(uuidv7())
  const state = String(uuidv7())
  sessionStorage.setItem(keycloakConfig.oauthNonceStorageKey, nonce)
  sessionStorage.setItem(keycloakConfig.oauthStateStorageKey, state)
  const redirectUri = keycloakConfig.redirectUri
  const loginUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth?client_id=${keycloakConfig.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid&nonce=${nonce}&state=${state}&response_mode=fragment&prompt=login`
  window.location.href = loginUrl
}

/** OAuth callback params may live in `location.hash`, in Vue Router's `route.hash`, or in `search`. */
function getOAuthCallbackQueryString(): { qs: string; strip: 'fragment' | 'query' } | null {
  const locHash = window.location.hash
  const routeHash = router.currentRoute.value.hash
  const search = window.location.search

  // Require content after `#`; `hash === '#'` must not be treated as a fragment (slice(1) would be '').
  if (locHash.length > 1) {
    return { qs: locHash.slice(1), strip: 'fragment' }
  }
  const fromRoute =
    routeHash.length > 0 ? (routeHash.startsWith('#') ? routeHash.slice(1) : routeHash) : ''
  if (fromRoute.length > 0) {
    return { qs: fromRoute, strip: 'fragment' }
  }
  if (search.length > 0 && (search.includes('code=') || search.includes('state='))) {
    return { qs: search.slice(1), strip: 'query' }
  }
  return null
}

export interface KeycloakAuthCallbackPayload {
  // Values from a validated OAuth redirect, for the BFF token exchange (and optional nonce checks).

  code: string
  // Authorization code from the IdP (query or fragment).

  nonce: string
  // Nonce read from sessionStorage; must match the ID token when the BFF validates.
}

/** Get authorization code. Validates state.
 * Reads from URL fragment (#) or query (?); Keycloak may use either depending on response_mode.
 * Remove auth params from the address bar after using the code.
 * @returns payload with `code` and stored `nonce` if validation passed and URL was cleaned;
 *   `null` without navigation when the URL has no OAuth callback indicators at all
 *   (i.e. the user is simply visiting the page rather than returning from Keycloak);
 *   `null` after routing to an error page when the callback is present but malformed.
 */
export function parseAuthResponse(): KeycloakAuthCallbackPayload | null {
  const parsed = getOAuthCallbackQueryString()
  if (!parsed) {
    // No OAuth callback markers on this load — user navigated here directly
    // (e.g. returning to the Welcome tab after login). Do not route to an
    // error page; let the caller render the normal welcome UI.
    return null
  }

  const params = new URLSearchParams(parsed.qs)

  // Extract code
  const code = params.get('code')
  if (!code) {
    router.push({ path: errorPathForCode(ErrorCode.NoCodeInAuthResponse) })
    return null
  }

  /**
   * Validate `state` (OAuth CSRF / session fixation defense).
   *
   * We generated a random `state` before redirecting to Keycloak and stored it in
   * sessionStorage. The callback must return the same value. Otherwise an attacker
   * could trick the user into loading a URL with an authorization `code` the attacker
   * obtained (e.g. for the attacker's account), and our app might complete login with
   * that code if we only trusted `code`.
   *
   * Example: Attacker completes login at Keycloak, copies
   * `https://our.app/welcome#code=ATTACKER_CODE&state=irrelevant`. They email the
   * victim a link with that fragment. Without matching `state` to our stored secret,
   * the app could treat ATTACKER_CODE as the victim's successful sign-in.
   */
  const state = params.get('state')
  if (!state) {
    router.push({ path: errorPathForCode(ErrorCode.NoStateInAuthResponse) })
    return null
  }
  const storedState = sessionStorage.getItem(keycloakConfig.oauthStateStorageKey)
  sessionStorage.removeItem(keycloakConfig.oauthStateStorageKey)
  if (state !== storedState) {
    router.push({ path: errorPathForCode(ErrorCode.InvalidStateInAuthResponse) })
    return null
  }

  // Extract session state
  const sessionState = params.get('session_state')
  if (!sessionState) {
    router.push({ path: errorPathForCode(ErrorCode.NoSessionStateInAuthResponse) })
    return null
  }
  sessionStorage.setItem(keycloakConfig.oauthSessionStateStorageKey, sessionState)

  /**
   * Validate `iss` the same way in spirit: bind this callback to our configured IdP.
   * A malicious or lookalike issuer could otherwise appear in the URL and try to pass
   * off a code from an attacker-controlled server as legitimate.
   */
  const iss = params.get('iss')
  if (!iss) {
    router.push({ path: errorPathForCode(ErrorCode.NoIssInAuthResponse) })
    return null
  }
  if (iss !== keycloakConfig.issuer) {
    router.push({ path: errorPathForCode(ErrorCode.InvalidIssInAuthResponse) })
    return null
  }

  const nonce = sessionStorage.getItem(keycloakConfig.oauthNonceStorageKey)
  if (!nonce) {
    router.push({ path: errorPathForCode(ErrorCode.NoNonceInStorage) })
    return null
  }

  const cleanUrl =
    parsed.strip === 'fragment'
      ? window.location.pathname + window.location.search
      : window.location.pathname
  window.history.replaceState({}, document.title, cleanUrl)
  return { code, nonce }
}
